import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";

import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject } from "../../utils/helpers.js";
import AlertDialog from "../dialogs/alertDialog.js";
import BotpressProductsDialog from "../dialogs/botpressProductsDialog.js";
import BotpressProductsTable from "../ui/botpressProductsTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const convertDateOnlyToTimestamp = (value, useCurrentTime = false) => {
  if (value == null || value === "") {
    return value;
  }

  const stringValue = String(value).trim();

  if (DATE_ONLY_PATTERN.test(stringValue)) {
    const [year, month, day] = stringValue.split("-").map(part => Number(part));

    if (useCurrentTime) {
      const now = new Date();

      return new Date(
        year,
        month - 1,
        day,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds(),
      ).toISOString();
    }

    return new Date(year, month - 1, day).toISOString();
  }

  return value;
};

const cleanProductRecord = productRecord => {
  if (productRecord == null) {
    return {};
  }

  // Drop server-computed columns; they are recomputed on write.
  const { computed, stale, matchSignals, softRequirements, ...cleanProduct } = productRecord;

  const cleanedProduct = {
    ...cleanProduct,
    assets: cleanProduct.assets || {
      cta: "",
      hero: "",
      thumbnail: "",
      disclaimer: "",
    },
    markers: Array.isArray(cleanProduct.markers) ? cleanProduct.markers : [],
  };

  if (cleanedProduct.priority == null || cleanedProduct.priority === "") {
    delete cleanedProduct.priority;
  }

  return cleanedProduct;
};

const BotpressProducts = ({
  products,
  productPagination,
  productPageLoading,
  searchProductsRequest,
  updateProductRequest,
  deleteProductsRequest,
  widgetSize = "medium",
  minHeight = "333px",
}) => {
  const [rows, setRows] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDeleteProductIDs, setSelectedDeleteProductIDs] = useState(null);

  useEffect(() => {
    if (products != null) {
      setRows(products.map(productRecord => cleanProductRecord(productRecord)));
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const onRequestNewPage = async pagination => {
    try {
      searchProductsRequest(pagination);
    } catch (error) {
      console.error("Error getting product table rows", error);
      setRows([]);
    }
  };

  const onRefreshTable = () => {
    let newPagination = deepCopyObject(productPagination || {});
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };

  const onCreateProduct = () => {
    const newProduct = {
      id: null,
      type: "",
      archived: false,
      title: "",
      summary: "",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "",
      url: "",
      assets: {
        cta: "",
        hero: "",
        thumbnail: "",
        disclaimer: "",
      },
      markers: [],
    };

    console.log("BOTPRESS PRODUCTS creating local product record", newProduct);
    setSelectedEntry(newProduct);
  };

  const onAcceptProduct = theRecord => {
    const isNewProduct = selectedEntry?.id == null;

    const productRecord = cleanProductRecord({
      ...theRecord,
      id: isNewProduct ? null : theRecord.id,
      startDate: convertDateOnlyToTimestamp(theRecord.startDate, isNewProduct),
      endDate: convertDateOnlyToTimestamp(theRecord.endDate),
      assets: theRecord.assets || {
        cta: "",
        hero: "",
        thumbnail: "",
        disclaimer: "",
      },
      markers: Array.isArray(theRecord.markers) ? theRecord.markers : [],
    });

    if (productRecord.priority == null || productRecord.priority === "") {
      delete productRecord.priority;
    }

    console.log("BOTPRESS PRODUCTS onAcceptProduct fired", {
      isNewProduct,
      productRecord,
    });

    updateProductRequest({ productRecord });
    setSelectedEntry(null);

    setTimeout(() => {
      onRefreshTable();
    }, 500);
  };

  const onDeleteProducts = productIDs => {
    deleteProductsRequest({ productIDs: productIDs });
    setSelectedDeleteProductIDs(null);

    setTimeout(() => {
      onRefreshTable();
    }, 500);
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Campaigns"}
        text={rows != null ? `${rows.length} Promotions` : "Promotions"}
      >
        <OmniResponsiveButton
          icon={<Add />}
          tooltipTitle="Create a new campaign"
          buttonText="Campaign"
          onClick={onCreateProduct}
        />
      </WidgetTitle>

      {rows == null && <WidgetSkeleton />}

      {rows != null && (
        <WidgetSurveyContent>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              opacity: productPageLoading ? 0.45 : 1,
              pointerEvents: productPageLoading ? "none" : "auto",
              transition: "opacity 0.25s ease",
            }}
          >
            <BotpressProductsTable
              data={rows}
              onRequestNewPage={onRequestNewPage}
              dataReduxPagination={productPagination}
              onRefreshTable={onRefreshTable}
              onEditEntry={setSelectedEntry}
            />
          </Box>
        </WidgetSurveyContent>
      )}

      {selectedEntry != null && (
        <BotpressProductsDialog
          open={selectedEntry != null}
          productRecord={selectedEntry}
          dialogTitle={selectedEntry.id == null ? "Create Campaign" : "Edit Campaign"}
          okMessage={selectedEntry.id == null ? "Create" : "Ok"}
          onAccept={onAcceptProduct}
          onDelete={theRecord => {
            setSelectedEntry(null);

            if (theRecord.id != null) {
              setSelectedDeleteProductIDs([theRecord.id]);
            }
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedDeleteProductIDs != null && (
        <AlertDialog
          open={selectedDeleteProductIDs != null}
          dialogTitle={"Confirm"}
          dialogMessage={
            selectedDeleteProductIDs.length === 1
              ? "Are you sure you want to delete this Campaign?"
              : `Are you sure you want to delete these ${selectedDeleteProductIDs.length} Campaigns?`
          }
          onAccept={() => {
            onDeleteProducts(selectedDeleteProductIDs);
          }}
          onClose={() => {
            setSelectedDeleteProductIDs(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  products: state.botpressSession.products,
  productPagination: state.botpressSession.productPagination,
  productPageLoading: state.botpressSession.productPageLoading,
});

const mapDispatchToProps = {
  searchProductsRequest: BotpressSessionActions.searchProductsRequest,
  updateProductRequest: BotpressSessionActions.updateProductRequest,
  deleteProductsRequest: BotpressSessionActions.deleteProductsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressProducts);
